import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface SLA {
  id: string
  name: string
  service: string
  type: string
  target: number
  current: number
  priority: string
  status: string
}

interface Breach {
  id: string
  sla_id: string
  severity: string
  status: string
  breach_date: string
  impact_duration_mins: number
  penalty_amount: number
}

interface Statistics {
  total_slas: number
  slas_met: number
  slas_at_risk: number
  slas_breached: number
  compliance_rate: number
  total_breaches: number
  open_breaches: number
  total_penalties: number
}

export default function SLAManagementPage() {
  const [slas, setSlas] = useState<SLA[]>([])
  const [breaches, setBreaches] = useState<Breach[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'slas' | 'breaches'>('slas')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [slasRes, breachesRes, statsRes] = await Promise.all([
        fetch('/api/slamanagement/slas'),
        fetch('/api/slamanagement/breaches'),
        fetch('/api/slamanagement/statistics')
      ])

      if (slasRes.ok) setSlas(await slasRes.json())
      if (breachesRes.ok) setBreaches(await breachesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching SLA management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      met: 'bg-green-100 text-green-800',
      at_risk: 'bg-yellow-100 text-yellow-800',
      breached: 'bg-red-100 text-red-800',
      open: 'bg-red-100 text-red-800',
      investigating: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    }
    return classes[status.toLowerCase().replace(' ', '_')] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SLA Management</h1>
          <p className="mt-2 text-gray-600">Monitor service level agreements and compliance</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total SLAs</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_slas}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">SLAs Met</p>
              <p className="text-2xl font-bold text-green-600">{statistics.slas_met}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance Rate</p>
              <p className="text-2xl font-bold text-blue-600">{Math.round(statistics.compliance_rate * 100)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Penalties</p>
              <p className="text-2xl font-bold text-red-600">${statistics.total_penalties.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('slas')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'slas'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              SLAs ({slas.length})
            </button>
            <button
              onClick={() => setActiveTab('breaches')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'breaches'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Breaches ({breaches.length})
            </button>
          </nav>
        </div>

        {/* SLAs Table */}
        {activeTab === 'slas' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {slas.map((sla) => (
                  <tr key={sla.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{sla.name}</p>
                      <p className="text-sm text-gray-500">{sla.type}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sla.service}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sla.target}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-bold ${sla.current >= sla.target ? 'text-green-600' : 'text-red-600'}`}>
                        {sla.current}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(sla.status)}`}>
                        {sla.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Breaches Table */}
        {activeTab === 'breaches' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Penalty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {breaches.map((breach) => (
                  <tr key={breach.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {breach.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {breach.sla_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        breach.severity === 'Critical' ? 'bg-red-100 text-red-800' :
                        breach.severity === 'Major' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {breach.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(breach.status)}`}>
                        {breach.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {breach.impact_duration_mins} mins
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      ${breach.penalty_amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
